import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { X, TrendingUp, ShoppingCart, Share2, UserPlus, Search } from "lucide-react";

const FLAGS = {
  SK:"🇸🇰",CZ:"🇨🇿",DE:"🇩🇪",PL:"🇵🇱",HU:"🇭🇺",FR:"🇫🇷",ES:"🇪🇸",PT:"🇧🇷",IT:"🇮🇹",
  NL:"🇳🇱",SE:"🇸🇪",NO:"🇳🇴",DK:"🇩🇰",FI:"🇫🇮",RO:"🇷🇴",BG:"🇧🇬",HR:"🇭🇷",UA:"🇺🇦",
  TR:"🇹🇷",JP:"🇯🇵",KR:"🇰🇷",CN:"🇨🇳",IN:"🇮🇳",SA:"🇸🇦",ID:"🇮🇩",
  GB:"🇬🇧",US:"🇺🇸",CA:"🇨🇦",AU:"🇦🇺",BR:"🇧🇷",MX:"🇲🇽",AR:"🇦🇷",
  IE:"🇮🇪",AT:"🇦🇹",CH:"🇨🇭",BE:"🇧🇪",GR:"🇬🇷",IL:"🇮🇱",AE:"🇦🇪",
};

const ACTION_ICONS = {
  scan: Search,
  order: ShoppingCart,
  share: Share2,
  register: UserPlus,
};

const ACTION_COLORS = {
  scan: "border-emerald-800/50 bg-emerald-900/10",
  order: "border-[#FFE600]/50 bg-[#FFE600]/5",
  share: "border-blue-800/50 bg-blue-900/10",
  register: "border-purple-800/50 bg-purple-900/10",
};

export function SocialProofPopup({ language = "en" }) {
  const [notification, setNotification] = useState(null);
  const [visible, setVisible] = useState(false);
  const [queue, setQueue] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, interval: 8000, types: ["scan","order","share","register"] });

  // Fetch settings and notifications
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const r = await axios.get(`${API}/social-proof/settings`);
        if (r.data) setSettings(prev => ({ ...prev, ...r.data }));
      } catch {}
    };
    const fetchNotifications = async () => {
      try {
        const r = await axios.get(`${API}/social-proof/feed?lang=${encodeURIComponent(language || "en")}`);
        if (r.data?.notifications) setQueue(r.data.notifications);
      } catch {}
    };
    fetchSettings();
    fetchNotifications();
  }, [language]);

  // Rotate notifications
  useEffect(() => {
    if (!settings.enabled || queue.length === 0) return;
    let idx = 0;
    const show = () => {
      setNotification(queue[idx % queue.length]);
      setVisible(true);
      setTimeout(() => setVisible(false), 5000);
      idx++;
    };
    // First one after 5 seconds
    const firstTimer = setTimeout(show, 5000);
    const interval = setInterval(show, settings.interval + 5000);
    return () => { clearTimeout(firstTimer); clearInterval(interval); };
  }, [queue, settings]);

  const handleClose = () => setVisible(false);

  if (!visible || !notification) return null;

  const Icon = ACTION_ICONS[notification.type] || Search;
  const colorClass = ACTION_COLORS[notification.type] || ACTION_COLORS.scan;
  const flag = FLAGS[notification.country] || "🌍";

  return (
    <div
      className={`fixed bottom-6 left-6 z-[9999] max-w-sm border ${colorClass} backdrop-blur-xl shadow-2xl shadow-black/50 transition-all duration-500 ${visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
      data-testid="social-proof-popup"
      style={{ animation: visible ? 'slideInLeft 0.5s ease-out' : '' }}
    >
      <style>{`@keyframes slideInLeft{from{transform:translateX(-100%);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
      <div className="flex items-start gap-3 p-4 pr-10">
        {/* Icon */}
        <div className="w-10 h-10 flex items-center justify-center bg-[var(--color-surface)] border border-[var(--color-border)] flex-shrink-0">
          <Icon className="w-5 h-5 text-[var(--color-text-muted)]" />
        </div>

        {/* Content */}
        <div className="min-w-0">
          <p className="text-white text-sm font-medium leading-tight">
            <span className="text-lg mr-1">{flag}</span>
            <span className="font-bold font-['Cabinet_Grotesk']">{notification.name}</span>
            {' '}<span className="text-[var(--color-text-muted)]">{notification.message}</span>
          </p>
          <p className="text-[var(--color-text-muted)] text-[11px] font-['JetBrains_Mono'] mt-1">{notification.time}</p>
        </div>

        {/* Close */}
        <button onClick={handleClose} className="absolute top-2 right-2 text-zinc-700 hover:text-[var(--color-text-muted)]" data-testid="social-proof-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Bottom bar */}
      <div className="h-0.5 bg-[var(--color-surface-alt)]">
        <div className="h-full bg-[#a855f7] animate-[shrink_5s_linear_forwards]" style={{animation:'shrink 5s linear forwards'}} />
        <style>{`@keyframes shrink{from{width:100%}to{width:0%}}`}</style>
      </div>
    </div>
  );
}
